import React, { useState, useMemo } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../firebase';
import Logo from '../../assets/logo.svg';
import '../../styles/AuthPage.css';

const ADMIN_EMAIL = 'sam@beginthework.com';
const BIOMETRIC_STORAGE_KEY = 'admin_biometric_enabled';

function isBiometricEnabled() {
  try {
    return localStorage.getItem(BIOMETRIC_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

function AdminLogin({ onSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);

  const isPasswordValid = password.length >= 12;
  const isEmailValid = email.includes('@') && email.includes('.');
  const isFormValid = isEmailValid && isPasswordValid;

  const hasCredentialsAPI = useMemo(
    () => typeof navigator !== 'undefined' && 'credentials' in navigator && typeof navigator.credentials.get === 'function',
    []
  );

  const showBiometric = hasCredentialsAPI && isBiometricEnabled();

  const storeCredentials = async (emailToStore, passwordToStore) => {
    try {
      if ('credentials' in navigator && 'PasswordCredential' in window) {
        const cred = new window.PasswordCredential({
          id: emailToStore,
          password: passwordToStore,
          name: emailToStore,
        });
        await navigator.credentials.store(cred);
      }
    } catch (err) {
      // Silently fail – credential storage is optional
    }
  };

  const signInWithStoredCredential = async () => {
    if (!hasCredentialsAPI) return;
    setError('');
    setBiometricLoading(true);

    try {
      const cred = await navigator.credentials.get({
        password: true,
        mediation: 'required',
      });
      if (!cred || typeof cred.password !== 'string') {
        setError('No saved sign-in found. Sign in with email and password first.');
        setBiometricLoading(false);
        return;
      }
      await signInWithEmailAndPassword(auth, cred.id, cred.password);
      const user = auth.currentUser;
      if (user && user.email === ADMIN_EMAIL) {
        onSuccess?.();
      }
      // Keep loading — onAuthStateChanged in AdminLayout handles the transition.
    } catch (err) {
      setError(err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found'
        ? 'Invalid email or password.'
        : err.message || 'Sign-in failed.');
      setBiometricLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      const user = auth.currentUser;
      if (user && user.email === ADMIN_EMAIL) {
        if (isBiometricEnabled()) {
          await storeCredentials(email, password);
        }
        onSuccess?.();
      }
      // Keep loading — onAuthStateChanged in AdminLayout handles the transition.
    } catch (err) {
      setError(err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found'
        ? 'Invalid email or password.'
        : err.message || 'Sign-in failed.');
      setLoading(false);
    }
  };

  return (
    <>
      {/* Logo above the modal */}
      <div className="admin-login-logo-wrap">
        <img src={Logo} alt="Begin Strength" className="admin-login-logo" />
      </div>

      {/* Login card */}
      <div className="admin-login-modal">
        <h2 className="admin-login-modal-title">Admin</h2>
        <p className="text-secondary" style={{ textAlign: 'center', margin: '0 0 var(--space-6)', fontSize: 'var(--font-size-sm)' }}>
          Sign in to manage programs, content, and settings.
        </p>

        <form onSubmit={handleSubmit} autoComplete="on" method="post" action="#">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <div className="auth-input-wrapper">
              <input
                type="email"
                name="email"
                id="admin-email"
                autoComplete="username email webauthn"
                inputMode="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="auth-input"
                required
              />
            </div>

            <div className="auth-input-wrapper auth-password-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                id="admin-password"
                autoComplete="current-password webauthn"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="auth-input"
                required
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>

            <p className={`auth-password-hint ${isPasswordValid ? 'valid' : ''}`}>
              {isPasswordValid ? (
                <>
                  <svg className="auth-check-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  12+ characters
                </>
              ) : (
                '12+ characters required'
              )}
            </p>

            {error && <p className="auth-error" role="alert">{error}</p>}

            <button
              type="submit"
              className="auth-submit"
              disabled={!isFormValid || loading}
            >
              {loading ? 'Please wait...' : 'Sign in'}
            </button>

            {showBiometric && (
              <>
                <p className="auth-biometric-divider">— or —</p>
                <button
                  type="button"
                  className="auth-submit auth-submit--secondary"
                  onClick={signInWithStoredCredential}
                  disabled={biometricLoading}
                  aria-label="Sign in with Face ID or Touch ID"
                >
                  {biometricLoading ? 'Please wait...' : 'Sign in with Face ID / Touch ID'}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </>
  );
}

export default AdminLogin;
